import React from 'react';
import { CheckCircle, Clock } from 'lucide-react';

const ProcesVerbalDetails = ({ procesVerbal }) => {
  const mentions = [
    { value: 'AJOURNE', label: 'Ajourné', color: 'bg-red-100 text-red-800' },
    { value: 'PASSABLE', label: 'Passable', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'ASSEZ_BIEN', label: 'Assez bien', color: 'bg-blue-100 text-blue-800' },
    { value: 'BIEN', label: 'Bien', color: 'bg-green-100 text-green-800' },
    { value: 'TRES_BIEN', label: 'Très bien', color: 'bg-emerald-100 text-emerald-800' },
    { value: 'EXCELLENT', label: 'Excellent', color: 'bg-purple-100 text-purple-800' }
  ];

  const decisions = [
    { value: 'ADMIS', label: 'Admis', color: 'bg-green-100 text-green-800' },
    { value: 'ADMIS_RESERVE', label: 'Admis avec réserve', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'AJOURNE', label: 'Ajourné', color: 'bg-red-100 text-red-800' },
    { value: 'REPORTE', label: 'Reporté', color: 'bg-orange-100 text-orange-800' }
  ];

  const getMentionInfo = (mention) => {
    return mentions.find(m => m.value === mention) || mentions[0];
  };

  const getDecisionInfo = (decision) => {
    return decisions.find(d => d.value === decision) || decisions[0];
  };

  const getSoutenanceLabel = (soutenance) => {
    if (!soutenance) return 'Non définie';
    
    let etudiantsNoms = '';
    if (soutenance.etudiants_soutenance && soutenance.etudiants_soutenance.length > 0) {
      const etudiant = soutenance.etudiants_soutenance[0].etudiant;
      etudiantsNoms = `${etudiant.nom} ${etudiant.prenom}`;
    } else if (soutenance.etudiant_principal_info) {
      etudiantsNoms = `${soutenance.etudiant_principal_info.nom} ${soutenance.etudiant_principal_info.prenom}`;
    }
    
    return etudiantsNoms || soutenance.titre_travail?.substring(0, 40) || 'Soutenance';
  };

  if (!procesVerbal) {
    return <div>Aucun procès-verbal sélectionné</div>;
  }

  const mentionInfo = getMentionInfo(procesVerbal.mention);
  const decisionInfo = getDecisionInfo(procesVerbal.decision);

  return (
    <div className="space-y-6">
      {/* En-tête du procès-verbal */}
      <div className="bg-gray-50 p-6 rounded-lg">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Procès-verbal - {getSoutenanceLabel(procesVerbal.soutenance)}
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Note finale</label>
            <p className="mt-1 text-lg font-bold text-blue-600">{procesVerbal.note_finale}/20</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Mention</label>
            <p className="mt-1 text-sm">
              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${mentionInfo.color}`}>
                {mentionInfo.label}
              </span>
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Décision</label>
            <p className="mt-1 text-sm">
              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${decisionInfo.color}`}>
                {decisionInfo.label}
              </span>
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Statut</label>
            <p className="mt-1 text-sm">
              {procesVerbal.valide ? (
                <span className="text-green-800 flex items-center">
                  <CheckCircle className="h-4 w-4 mr-1" />
                  Validé
                </span>
              ) : (
                <span className="text-yellow-800 flex items-center">
                  <Clock className="h-4 w-4 mr-1" />
                  En attente
                </span>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Informations sur la soutenance */}
      {procesVerbal.soutenance && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Informations sur la soutenance</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {procesVerbal.soutenance.titre_travail && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Titre du travail</label>
                <p className="mt-1 text-sm text-gray-900">{procesVerbal.soutenance.titre_travail}</p>
              </div>
            )}
            
            {procesVerbal.soutenance.date_soutenance && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Date de soutenance</label>
                <p className="mt-1 text-sm text-gray-900">
                  {new Date(procesVerbal.soutenance.date_soutenance).toLocaleDateString('fr-FR')}
                </p>
              </div>
            )}
            
            {procesVerbal.heure_debut && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Heure</label>
                <p className="mt-1 text-sm text-gray-900">
                  {procesVerbal.heure_debut.slice(0, 5)}
                  {procesVerbal.heure_fin && ` - ${procesVerbal.heure_fin.slice(0, 5)}`}
                </p>
              </div>
            )}

            {procesVerbal.soutenance.salle && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Lieu</label>
                <p className="mt-1 text-sm text-gray-900">{procesVerbal.soutenance.salle.nom}</p>
              </div>
            )}

            {procesVerbal.soutenance.type_soutenance && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Type de soutenance</label>
                <p className="mt-1 text-sm text-gray-900">{procesVerbal.soutenance.type_soutenance.nom}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Observations et recommandations */}
      {(procesVerbal.observations || procesVerbal.recommandations) && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Observations et recommandations</h3>
          
          {procesVerbal.observations && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700">Observations</label>
              <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{procesVerbal.observations}</p>
            </div>
          )}
          
          {procesVerbal.recommandations && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Recommandations</label>
              <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{procesVerbal.recommandations}</p>
            </div>
          )}
        </div>
      )}

      {/* Signatures du jury */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Signatures du jury</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <p className="font-medium text-gray-900 mb-2">Président</p>
            <div className="h-16 border-b-2 border-gray-400 mt-4 mb-2"></div>
            <p className="text-sm text-gray-600">
              {procesVerbal.date_signature_president ? 
                `Le ${new Date(procesVerbal.date_signature_president).toLocaleDateString('fr-FR')}` : 
                'Non signé'
              }
            </p>
          </div>
          <div className="text-center">
            <p className="font-medium text-gray-900 mb-2">Rapporteur</p>
            <div className="h-16 border-b-2 border-gray-400 mt-4 mb-2"></div>
            <p className="text-sm text-gray-600">
              {procesVerbal.date_signature_rapporteur ? 
                `Le ${new Date(procesVerbal.date_signature_rapporteur).toLocaleDateString('fr-FR')}` : 
                'Non signé'
              }
            </p>
          </div>
          <div className="text-center">
            <p className="font-medium text-gray-900 mb-2">Examinateur</p>
            <div className="h-16 border-b-2 border-gray-400 mt-4 mb-2"></div>
            <p className="text-sm text-gray-600">
              {procesVerbal.date_signature_examinateur ? 
                `Le ${new Date(procesVerbal.date_signature_examinateur).toLocaleDateString('fr-FR')}` : 
                'Non signé'
              }
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProcesVerbalDetails; 